import React, { useEffect, useMemo, useState } from 'react';
import { Check, X, Calendar, Search, ChevronLeft, ChevronRight, X as XIcon } from 'lucide-react';

// Import shared types
import { LeaveListProps } from '../../components/common/types';
import { fmtDate } from '../../utils/datetime';

const PER_PAGE = 10;

const LeaveList: React.FC<LeaveListProps> = ({ requests, isLoading, userRole, onStatusUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('');
  const [page, setPage] = useState(1);

  const leaveTypes = useMemo(
    () => Array.from(new Set(requests.map((r) => r.leave_type).filter(Boolean))),
    [requests]
  );

  const filteredRequests = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return requests.filter((request) => {
      const employeeName = (request as any).employee_name || request.created_by || '';
      const matchesSearch =
        q === '' ||
        employeeName.toLowerCase().includes(q) ||
        request.leave_type?.toLowerCase().includes(q) ||
        request.reason?.toLowerCase().includes(q);
      const matchesType = !leaveTypeFilter || request.leave_type === leaveTypeFilter;
      return matchesSearch && matchesType;
    });
  }, [requests, searchTerm, leaveTypeFilter]);

  // Reset to page 1 whenever the filters or the underlying data (e.g. active tab) change.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, leaveTypeFilter, requests]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const paginatedRequests = filteredRequests.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const hasActiveFilters = !!(searchTerm || leaveTypeFilter);
  const clearFilters = () => {
    setSearchTerm('');
    setLeaveTypeFilter('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'Rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleApprove = (request: any) => {
    console.log('Approving leave request:', request.id);
    onStatusUpdate(request, 'Approved', 'Leave approved by manager');
  };

  const handleReject = (request: any) => {
    console.log('Rejecting leave request:', request.id);
    onStatusUpdate(request, 'Rejected', 'Leave rejected by manager');
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          Leave Requests
        </h3>
        <div className="space-y-4">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="space-y-2 flex-1">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-48"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md">
      <div className="p-6">
        {/* Header */}
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          Leave Requests ({filteredRequests.length}{hasActiveFilters ? ` / ${requests.length}` : ''})
        </h3>

        {/* Search + Filter */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by employee, leave type, or reason..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={leaveTypeFilter}
            onChange={(e) => setLeaveTypeFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">All Leave Types</option>
            {leaveTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
              <XIcon className="w-4 h-4 mr-1" />
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Employee</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Leave Type</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Duration</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Days</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Reason</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRequests.map((request) => (
                <tr key={request.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                  <td className="py-4 px-4">
                    <div className="font-medium text-gray-900">
                      {(request as any).employee_name || request.created_by || 'Unknown'}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm border">
                      {request.leave_type}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-sm">
                      <div className="font-medium">{fmtDate(request.start_date)}</div>
                      {request.end_date && request.end_date !== request.start_date && (
                        <div className="text-gray-500">to {fmtDate(request.end_date)}</div>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="font-medium text-gray-900">
                      {request.total_days} days
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="max-w-40 truncate" title={request.reason}>
                      {request.reason}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-2 py-1 rounded text-sm border ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex gap-2">
                      {userRole === 'admin' && request.status === 'Pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(request)}
                            className="p-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                            title="Approve leave request"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleReject(request)}
                            className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                            title="Reject leave request"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRequests.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {hasActiveFilters ? 'No leave requests match your search/filters.' : 'No leave requests found'}
            </p>
          </div>
        )}

        {/* Pagination */}
        {filteredRequests.length > 0 && totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 mt-2 border-t border-gray-100">
            <p className="text-sm text-gray-600">
              Showing {(safePage - 1) * PER_PAGE + 1}–{Math.min(safePage * PER_PAGE, filteredRequests.length)} of{' '}
              <span className="font-medium">{filteredRequests.length}</span>
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(safePage - 1)}
                disabled={safePage === 1}
                className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                .map((p, idx, arr) => (
                  <React.Fragment key={p}>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                      <span className="px-1 text-gray-400 text-sm">…</span>
                    )}
                    <button
                      onClick={() => setPage(p)}
                      className={`min-w-[32px] px-2 py-1.5 text-sm rounded-lg ${
                        p === safePage ? 'bg-blue-600 text-white font-medium' : 'border border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}

              <button
                onClick={() => setPage(safePage + 1)}
                disabled={safePage === totalPages}
                className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveList;
